import { Component, DestroyRef, ElementRef, HostListener, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '@core/services/api.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';

@Component({
  standalone: false,
  selector: 'app-exceptions',
  templateUrl: './exceptions.component.html',
  styleUrls: ['./exceptions.component.scss']
})
export class ExceptionsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private host = inject(ElementRef<HTMLElement>);

  excluded: any[] = [];
  loading = true;
  saving = false;

  private candidates: any[] = [];

  query = '';
  open = false;
  highlighted = 0;
  picked: any = null;

  constructor(
    private apiService: ApiService,
    private snackbar: SnackbarService,
    private confirm: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    forkJoin({
      excluded: this.apiService.get('/employees/exclusions').pipe(catchError(() => of([]))),
      visible:  this.apiService.get('/employees').pipe(catchError(() => of([])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.excluded = this.asList(res.excluded);
        this.candidates = this.asList(res.visible);
        this.loading = false;
      },
      error: () => {
        this.snackbar.showFailure('Could not load the exception list.');
        this.loading = false;
      }
    });
  }

  private asList(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  }


  get results(): any[] {
    const q = this.query.trim().toLowerCase();
    if (!q) return [];
    return this.candidates
      .filter(e => [this.fullName(e), e.email, e.employeeId, e.designation]
        .filter(Boolean).join(' ').toLowerCase().includes(q))
      .slice(0, 6);
  }

  onQueryChange(): void {
    this.picked = null;
    this.highlighted = 0;
    this.open = this.query.trim().length > 0;
  }

  pick(emp: any): void {
    this.picked = emp;
    this.query = `${this.fullName(emp)} · ${emp.email}`;
    this.open = false;
  }

  onKeydown(ev: KeyboardEvent): void {
    if (!this.open || !this.results.length) return;

    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      this.highlighted = (this.highlighted + 1) % this.results.length;
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      this.highlighted = (this.highlighted - 1 + this.results.length) % this.results.length;
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      this.pick(this.results[this.highlighted]);
    } else if (ev.key === 'Escape') {
      this.open = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent): void {
    if (!this.host.nativeElement.contains(ev.target as Node)) this.open = false;
  }

  clear(): void {
    this.query = '';
    this.picked = null;
    this.open = false;
  }


  add(): void {
    if (!this.picked || this.saving) return;

    const emp = this.picked;
    this.saving = true;
    this.apiService.post('/employees/exclusions', { email: emp.email })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.snackbar.showSuccess(`${this.fullName(emp)} is now hidden from all employee screens.`);
          this.clear();
          this.saving = false;
          this.load();
        },
        error: (err: any) => {
          this.saving = false;
          this.snackbar.showFailure(err?.error?.message || 'Could not add that exception.');
        }
      });
  }

  async remove(emp: any): Promise<void> {
    const name = this.fullName(emp);
    const ok = await this.confirm.confirm(
      `${name} will become visible again across all employee screens.`,
      { title: 'Remove exception', confirmLabel: 'Remove' }
    );
    if (!ok) return;

    this.apiService.delete(`/employees/exclusions/${emp.id}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.snackbar.showSuccess(`${name} is visible again.`);
          this.load();
        },
        error: () => this.snackbar.showFailure('Could not remove that exception.')
      });
  }


  fullName(emp: any): string {
    return [emp?.firstName, emp?.lastName].filter(Boolean).join(' ') || emp?.email || 'This user';
  }

  initials(emp: any): string {
    return this.fullName(emp).split(/\s+/).filter(Boolean).map(p => p[0]).join('').slice(0, 2).toUpperCase();
  }

  trackById(_: number, emp: any): string { return emp.id; }
}
